import { storeToRefs } from 'pinia'
import { type RouteRecordRaw } from 'vue-router'

import { type CoreLocation } from '@/core/store/types'
import { useCoreStore } from '@/core/store/useCoreStore'
import { assignmentsRouter } from '@/features/assignments/router/assignments.router'
import { applyMetaToChildren } from '@/utils/router'

export const toolsRouter: RouteRecordRaw[] = [
  {
    children: applyMetaToChildren(
      [
        {
          component: async () => import('../views/DeliveryToolView.vue'),
          meta: {
            title: 'tools.delivery',
          },
          name: 'brand.tools.delivery',
          path: 'delivery',
        },
        {
          component: async () => import('../views/SmsSubscriberToolView.vue'),
          meta: {
            title: 'tools.sms-subscriber-import',
          },
          name: 'brand.tools.sms-subscriber-import',
          path: 'sms-subscriber-import',
        },
        {
          component: async () => import('../views/DomainToolView.vue'),
          meta: {
            title: 'tools.dns',
          },
          name: 'brand.tools.dns',
          path: 'dns',
        },
        {
          component: async () =>
            import('../views/WorkflowScenariosToolView.vue'),
          meta: {
            title: 'tools.workflow-scenarios',
          },
          name: 'brand.tools.workflow-scenarios',
          path: 'workflow-scenarios',
        },
        {
          component: async () => import('../views/TipsToolView.vue'),
          meta: {
            title: 'tools.tips',
          },
          name: 'brand.tools.tips',
          path: 'tips',
        },
        {
          component: async () => import('../views/CloneMenuView.vue'),
          meta: {
            title: 'tools.clone-menu',
          },
          name: 'brand.tools.clone-menu',
          path: 'clone-menu',
        },
        {
          component: async () => import('../views/PruneMenuView.vue'),
          meta: {
            title: 'tools.prune-menu',
          },
          name: 'brand.tools.prune-menu',
          path: 'prune-menu',
        },
        {
          component: async () => import('../views/DeleteLocationBrand.vue'),
          meta: {
            title: 'tools.delete-location-brand',
          },
          name: 'brand.tools.delete-location-brand',
          path: 'delete-location-brand',
        },
        {
          component: async () => import('../views/CreateHotLaunchToolView.vue'),
          meta: {
            title: 'tools.hot-launch',
          },
          name: 'brand.tools.hot-launch',
          path: 'hot-launch',
        },
        {
          children: [
            {
              component: async () =>
                import('../views/onboarding/OnboardingActionsView.vue'),
              meta: {
                title: 'tools.onboarding',
              },
              name: 'brand.tools.onboarding.actions',
              path: 'actions',
            },
            {
              children: [
                {
                  component: async () =>
                    import('../views/onboarding/OnboardingWorkflowDetailView.vue'),
                  meta: {
                    title: 'tools.onboarding',
                  },
                  name: 'brand.tools.onboarding.workflows.detail',
                  path: ':workflowId',
                  props: true,
                },
              ],
              component: async () =>
                import('../views/onboarding/OnboardingWorkflowView.vue'),
              meta: {
                title: 'tools.onboarding',
              },
              name: 'brand.tools.onboarding.workflows',
              path: 'workflows',
            },
          ],
          component: async () =>
            import('../views/onboarding/OnboardingRoutingView.vue'),
          meta: {
            title: 'tools.onboarding',
          },
          name: 'brand.tools.onboarding',
          path: 'onboarding',
          redirect: {
            name: 'brand.tools.onboarding.actions',
          },
        },
        {
          component: async () => import('../views/ConversionCardsToolView.vue'),
          meta: {
            title: 'tools.conversion-cards',
          },
          name: 'brand.tools.conversion-cards',
          path: 'conversion-cards',
        },
        {
          component: async () =>
            import('../views/InStoreAppAdoptionMaterialsToolView.vue'),
          meta: {
            title: 'tools.in-store-app-adoption-materials',
          },
          name: 'brand.tools.in-store-app-adoption-materials',
          path: 'in-store-app-adoption-materials',
        },
        {
          component: async () => import('../views/VirtualCardToolView.vue'),
          meta: {
            title: 'tools.virtual-card',
          },
          name: 'brand.tools.virtual-card',
          path: 'virtual-card',
        },
        {
          component: async () => import('../views/AcceptancesToolView.vue'),
          meta: {
            title: 'tools.acceptances',
          },
          name: 'brand.tools.acceptances',
          path: 'acceptances',
        },
        {
          component: async () => import('../views/WebsiteImportExportView.vue'),
          meta: {
            title: 'tools.website-import-export',
          },
          name: 'brand.tools.website-import-export',
          path: 'website-import-export',
        },
        {
          component: async () => import('../views/ScoutView.vue'),
          meta: {
            title: 'tools.scout',
          },
          name: 'brand.tools.scout',
          path: 'scout',
        },
        {
          children: [
            {
              name: 'brand.tools.phone-ordering.overview',
              path: 'overview',
              redirect: (to) => ({
                name: 'brand.tools.phone-ordering',
                params: to.params,
                query: to.query,
              }),
            },
            {
              name: 'brand.tools.phone-ordering.calls',
              path: 'calls',
              redirect: (to) => ({
                name: 'brand.tools.phone-ordering',
                params: to.params,
                query: to.query,
              }),
            },
          ],
          component: async () => import('../views/PhoneOrderingView.vue'),
          meta: {
            title: 'tools.phone-ordering',
          },
          name: 'brand.tools.phone-ordering',
          path: 'phone-ordering',
        },
        ...assignmentsRouter,
        {
          component: async () =>
            import('../../admin/views/MfaDiagnosticsView.vue'),
          meta: {
            title: 'tools.mfa-diagnostics',
          },
          name: 'brand.tools.mfa-diagnostics',
          path: 'mfa-diagnostics',
        },
        {
          component: async () =>
            import('../../marketing/views/CampaignsOverviewViewV2.vue'),
          meta: {
            title: 'tools.marketing-insights',
          },
          name: 'brand.tools.marketing-insights',
          path: 'marketing-insights',
        },
        {
          component: async () => import('../views/AcceptanceDocumentView.vue'),
          meta: {
            title: 'tools.acceptance-document',
          },
          name: 'brand.tools.acceptance-document',
          path: 'acceptances/:acceptanceId',
          props: true,
        },
        {
          beforeEnter: async (to, from, next) => {
            // Check if we have a single location brand
            const { activeBrand } = storeToRefs(useCoreStore())
            const locations = activeBrand.value?.locations

            if (locations && locations.length === 1) {
              // Single location - redirect directly to the detail view
              return next({
                name: 'brand.tools.phone-tree.location',
                params: {
                  brandId: to.params.brandId,
                  locationId: (locations[0] as CoreLocation).id,
                },
              })
            }

            // Multi-location or no locations - show the selector
            return next()
          },
          component: async () =>
            import('../views/PhoneTreeLocationSelectionView.vue'),
          meta: {
            title: 'tools.phone-tree-location-selection',
          },
          name: 'brand.tools.phone-tree',
          path: 'phone-tree',
        },
        {
          children: [
            {
              component: async () =>
                import('../components/phone-tree/management/PhoneTreeKeepNumberManagement.vue'),
              meta: {
                title: 'tools.phone-tree.keep',
              },
              name: 'brand.tools.phone-tree.location.keep',
              path: 'keep',
              props: true,
            },
            {
              component: async () =>
                import('../components/phone-tree/management/PhoneTreeReplaceNumberManagement.vue'),
              meta: {
                title: 'tools.phone-tree.replace',
              },
              name: 'brand.tools.phone-tree.location.replace',
              path: 'replace',
              props: true,
            },
          ],
          component: async () => import('../views/PhoneTreeView.vue'),
          meta: {
            title: 'tools.phone-tree',
          },
          name: 'brand.tools.phone-tree.location',
          path: 'phone-tree/location/:locationId',
          props: true,
        },
      ],
      {
        authZ: ({ user }) => user.hasRole('pb:super'),
      }
    ),
    component: {
      name: 'ToolView',
      template: '<router-view />',
    },
    path: 'tools',
    redirect: { name: 'brand.tools.sms-subscriber-import' },
  },
]
